import {store} from "../store";
import {MidiDeviceOptionsArray, settingsSlice} from "./settingsSlice";
import {lessonLocalStorage} from "../../services/lesson/lessonLocalStorage";
import {volumesChanged} from "../../services/eventBus/settings/settingsEventBus";
import {localStorageService} from "../../services/storage/local-storage";
import storageKeys from "../../services/storage/storageKeys";
import {IVolumes} from "../types/IVolumes";
import {defaultVolumes} from "./settingsDefaults";
import {PDCStatus} from "../types/pdcStatus";

/** Responsible for setting / getting state that is used in multiple places in the app. */

export const settingsStore = {
  // State modifiers
  toggleBackingTrack: () => {
    const isBackingTrackMuted = settingsSlice.selectors.isBackingTrackMutedSelector(store.getState());
    const volumes = {...settingsSlice.selectors.volumesSelector(store.getState()), isBackingTrackMuted: !isBackingTrackMuted};

    // Update redux store
    store.dispatch(settingsSlice.actions.isBackingTrackMutedReducer({isMuted: volumes.isBackingTrackMuted}));

    // Update local storage
    updateVolumesValueInLocalStorage(volumes);

    // Emit event
    volumesChanged(volumes);
  },
  toggleMetronome: () => {
    const isMetronomeMuted = settingsSlice.selectors.isMetronomeMutedSelector(store.getState());
    const volumes = {...settingsSlice.selectors.volumesSelector(store.getState()), isMetronomeMuted: !isMetronomeMuted};

    // Update redux store
    store.dispatch(settingsSlice.actions.isMetronomeMutedReducer({isMuted: volumes.isMetronomeMuted}));

    // Update local storage
    updateVolumesValueInLocalStorage(volumes);

    // Emit event
    volumesChanged(volumes);
  },
  resetVolumes: () => {
    updateVolumesValueInLocalStorage({...defaultVolumes});
    store.dispatch(settingsSlice.actions.resetVolumesReducer());
    volumesChanged({...defaultVolumes});
  },
  setBackingTrackVolume: (volume: number) => {
    updateBackingTrackVolume(volume);
  },
  setCountInVolume: (volume: number) => {
    const volumes = {...settingsSlice.selectors.volumesSelector(store.getState()), countIn: volume};
    updateVolumesValueInLocalStorage(volumes);
    store.dispatch(settingsSlice.actions.countInVolumeReducer({volume: volume}));
    volumesChanged(volumes);
  },
  toggleCountIn: () => {
    const isCountInMuted = settingsSlice.selectors.isCountInMutedSelector(store.getState());
    const volumes = {...settingsSlice.selectors.volumesSelector(store.getState()), isCountInMuted: !isCountInMuted};

    // Update redux store
    store.dispatch(settingsSlice.actions.isCountInMutedReducer({isMuted: volumes.isCountInMuted}));

    // Update local storage
    updateVolumesValueInLocalStorage(volumes);

    // Emit event
    volumesChanged(volumes);
  },
  setMetronomeVolume: (volume: number) => {
    updateMetronomeVolume(volume);
  },
  setPianoVolume: (volume: number) => {
    const volumes = {...settingsSlice.selectors.volumesSelector(store.getState()), piano: volume};
    updateVolumesValueInLocalStorage(volumes);
    store.dispatch(settingsSlice.actions.pianoVolumeReducer({volume: volume}));
    volumesChanged(volumes);
  },
  setVideoVolume: (volume: number) => {
    const volumes = {...settingsSlice.selectors.volumesSelector(store.getState()), video: volume};
    updateVolumesValueInLocalStorage(volumes);
    store.dispatch(settingsSlice.actions.videoVolumeReducer({volume: volume}));
    volumesChanged(volumes);
  },
  setVolumes: (volumes: IVolumes) => {
    updateVolumesValueInLocalStorage(volumes);
    store.dispatch(settingsSlice.actions.volumesReducer({volumes}));
    volumesChanged(volumes);
  },
  setSelectedMidiDevice: (deviceId: string, deviceName: string) => {
    localStorageService.setItem<string>(storageKeys.settings_selectedMidiDeviceId, deviceId);
    localStorageService.setItem<string>(storageKeys.settings_selectedMidiDeviceName, deviceName);
    
    store.dispatch((settingsSlice.actions.selectedMidiDeviceIdReducer({deviceId})));
    store.dispatch((settingsSlice.actions.selectedMidiDeviceNameReducer({deviceName})));
  },
  setCurrentlySelectedSoundLibrary: (library: string) => {
    store.dispatch((settingsSlice.actions.currentlySelectedSoundLibraryReducer({library})));
  },
  setIsMidiDeviceConnected: (isConnected: boolean) => {
    store.dispatch((settingsSlice.actions.isMidiDeviceConnectedReducer({isConnected})));
  },
  setIsPitchDetectionDebuggingEnabled: (isEnabled: boolean) => {
    store.dispatch((settingsSlice.actions.isPitchDetectionDebuggingEnabledReducer({isEnabled})));
  },
  setMidiDeviceOptions: (options: MidiDeviceOptionsArray) => {
    store.dispatch((settingsSlice.actions.midiDeviceOptionsReducer({options})));
  },
  setIsUMPConsentRequired: (isRequired: boolean) => {
    store.dispatch((settingsSlice.actions.isUMPConsentRequiredReducer({isRequired})));
  },
  setIsPdcConsentApplicable: (isApplicable: boolean) => {
    store.dispatch((settingsSlice.actions.isPdcConsentApplicableReducer({isApplicable})));
  },
  setPDCStatus: (status: PDCStatus) => {
    localStorageService.setJsonItem<PDCStatus>(storageKeys.consent_PDCStatus, status);
    store.dispatch((settingsSlice.actions.pdcStatusReducer({status})));
  },
  setVirtualPianoNumberOfKeys: (numberOfKeys: number | string) => {
    const settingsForSelectedMidiDevice = lessonLocalStorage.getMidiDeviceSettingsById(settingsStore.getSelectedMidiDeviceId());

    // Update local storage if we can
    if (settingsForSelectedMidiDevice) {
      settingsForSelectedMidiDevice.numberOfKeys = numberOfKeys;
      lessonLocalStorage.upsertMidiDeviceSettings(settingsForSelectedMidiDevice);
    }

    store.dispatch((settingsSlice.actions.selectedNumberOfKeysReducer({numberOfKeys})));
  },

  // Getters
  getBackingTrackVolume: (): number => { const volumes = getVolumes(); return volumes.isBackingTrackMuted ? 0 : volumes.backingTrack; },
  getCountInVolume: (): number => { return getVolumes().countIn; },
  getSelectedMidiDeviceId: (): string => {
    const deviceIdLocalStorageValue = localStorageService.getOrCreateStringValue(storageKeys.settings_selectedMidiDeviceId, "");
    const deviceIdReduxValue = settingsSlice.selectors.selectedMidiDeviceIdSelector(store.getState());
    const shouldSyncIdValues = deviceIdLocalStorageValue.length && deviceIdLocalStorageValue !== deviceIdReduxValue;

    if (shouldSyncIdValues) {
      const deviceNameLocalStorageValue = localStorageService.getOrCreateStringValue(storageKeys.settings_selectedMidiDeviceName, "");
      settingsStore.setSelectedMidiDevice(deviceIdLocalStorageValue, deviceNameLocalStorageValue);
      return deviceIdLocalStorageValue;
    }

    return deviceIdReduxValue;
  },
  getSelectedMidiDeviceName: (): string => {
    const deviceNameLocalStorageValue = localStorageService.getOrCreateStringValue(storageKeys.settings_selectedMidiDeviceName, "");
    const deviceNameReduxValue = settingsSlice.selectors.selectedMidiDeviceNameSelector(store.getState());
    const shouldSyncNameValues = deviceNameLocalStorageValue.length && deviceNameLocalStorageValue !== deviceNameReduxValue;

    if (shouldSyncNameValues) {
      const deviceIdLocalStorageValue = localStorageService.getOrCreateStringValue(storageKeys.settings_selectedMidiDeviceId, "");
      settingsStore.setSelectedMidiDevice(deviceIdLocalStorageValue, deviceNameLocalStorageValue);
      return deviceNameLocalStorageValue;
    }

    return deviceNameReduxValue;
  },
  getIsBackingTrackMuted: (): boolean => { return settingsSlice.selectors.isBackingTrackMutedSelector(store.getState()); },
  getIsCountInMuted: (): boolean => { return settingsSlice.selectors.isCountInMutedSelector(store.getState()); },
  getIsMetronomeMuted: (): boolean => { return settingsSlice.selectors.isMetronomeMutedSelector(store.getState()); },
  getIsMidiDeviceConnected: (): boolean => { return settingsSlice.selectors.isMidiDeviceConnectedSelector(store.getState()); },
  getIsPitchDetectionDebuggingEnabled: (): boolean => { return settingsSlice.selectors.isPitchDetectionDebuggingEnabledSelector(store.getState()); },
  getMetronomeVolume: (): number => { const volumes = getVolumes(); return volumes.isMetronomeMuted ? 0 : getVolumes().metronome; },
  getMidiDeviceOptions: (): MidiDeviceOptionsArray => { return settingsSlice.selectors.midiDeviceOptionsSelector(store.getState()); },
  getPianoVolume: (): number => { return getVolumes().piano; },
  getPDCStatus: (): PDCStatus => {
    const pdcStatusFromLocalStorage = localStorageService.getJsonItem<PDCStatus>(storageKeys.consent_PDCStatus) ?? "unknown";
    const pdcStatusFromRedux = settingsSlice.selectors.pdcStatusSelector(store.getState());

    if (pdcStatusFromLocalStorage !== pdcStatusFromRedux) {
      store.dispatch((settingsSlice.actions.pdcStatusReducer({status: pdcStatusFromLocalStorage})));
      return pdcStatusFromLocalStorage;
    }

    return pdcStatusFromRedux;
  },
  getIsUMPConsentRequired: (): boolean => { return settingsSlice.selectors.isUMPConsentRequiredSelector(store.getState()); },
  getIsPdcConsentApplicable: (): boolean => { return settingsSlice.selectors.isPdcConsentApplicableSelector(store.getState()); },
  getVideoVolume: () => { return getVolumes().video; },
  getVolumes: (): IVolumes => { return getVolumes(); },
  getVirtualPianoNumberOfKeys: (): number | string => {
    const settingsForSelectedMidiDevice = lessonLocalStorage.getMidiDeviceSettingsById(settingsStore.getSelectedMidiDeviceId());
    const localStorageValue = settingsForSelectedMidiDevice?.numberOfKeys;
    const reduxValue = settingsSlice.selectors.virtualPianoNumberOfKeysSelector(store.getState());

    if (localStorageValue && localStorageValue !== reduxValue) {
      store.dispatch((settingsSlice.actions.selectedNumberOfKeysReducer({ numberOfKeys: localStorageValue})));
      return localStorageValue;
    }

    return reduxValue;
  },
}

function getVolumes(): IVolumes {
  const userVolumesFromLocalStorage = localStorageService.getJsonItem<IVolumes>(storageKeys.settings_volumes);

  // Get values from Redux store to use as initial values
  const finalUserVolumes = {...settingsSlice.selectors.volumesSelector(store.getState())};

  // Local storage is the source of truth. If there is a disagreement between redux and local storage, use
  // local storage and update redux. When new data points are added, local storage might be missing them. So update
  // local storage with the new data points.
  let isLocalStorageMissingValues = false;
  let updateRedux = false;

  for (const key in finalUserVolumes) {
    const doesLocalStorageHaveDataPoint = userVolumesFromLocalStorage && (key in userVolumesFromLocalStorage);

    if (doesLocalStorageHaveDataPoint) {
      // @ts-ignore
      const volume = userVolumesFromLocalStorage[key];
      // @ts-ignore
      const reduxVolume = finalUserVolumes[key];

      // Update redux
      if (reduxVolume !== volume) {
        updateRedux = true;

        // @ts-ignore
        finalUserVolumes[key] = volume;
      }
    } else {
      isLocalStorageMissingValues = true;
    }
  }

  // If localStorage was missing any volume values, update localStorage
  if (isLocalStorageMissingValues) {
    localStorageService.setJsonItem<IVolumes>(storageKeys.settings_volumes, finalUserVolumes);
  }

  if (updateRedux) {
    settingsStore.setVolumes(finalUserVolumes);
  }

  return finalUserVolumes;
}

function updateMetronomeVolume(volume: number): void {
  // Get current volumes
  const volumes = {...settingsSlice.selectors.volumesSelector(store.getState()), metronome: volume};
  // Update local storage
  updateVolumesValueInLocalStorage(volumes);
  // Update redux store
  store.dispatch(settingsSlice.actions.metronomeVolumeReducer({volume: volume}));
  // Emit event
  volumesChanged(volumes);
}

function updateBackingTrackVolume(volume: number): void {
  // Get current volumes
  const volumes = {...settingsSlice.selectors.volumesSelector(store.getState()), backingTrack: volume};
  // Update local storage
  updateVolumesValueInLocalStorage(volumes);
  // Update redux store
  store.dispatch(settingsSlice.actions.backingTrackVolumeReducer({volume: volume}));
  // Emit event
  volumesChanged(volumes);
}

function updateVolumesValueInLocalStorage(volumes: IVolumes): void {
  localStorageService.setJsonItem<IVolumes>(storageKeys.settings_volumes, volumes);
}
